import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Adds lines to the cart and lets the theme's cart drawer and icon refresh themselves.
 * @param {Element} source - Element the cart events bubble from.
 * @param {{ id: number, quantity: number, properties?: Record<string, string> }[]} lines
 * @returns {Promise<boolean>} Whether the add succeeded.
 */
async function addLines(source, lines) {
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(`${Theme.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines }),
    });
    const result = await response.json();
    const didError = !response.ok;

    if (didError) {
      source.dispatchEvent(
        new CartErrorEvent({ error: result.message || 'Add to cart failed', code: 'INVALID', detail: result })
      );
    }

    const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
      r.json()
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError, items: cart.items, source: 'party-girls' },
    });
    return !didError;
  } catch (error) {
    deferred.reject(error);
    return false;
  }
}

/**
 * Runs an add while reflecting progress on the button that triggered it.
 * @param {HTMLButtonElement} button
 * @param {Parameters<typeof addLines>[1]} lines
 */
async function addFromButton(button, lines) {
  const label = button.textContent;
  button.disabled = true;
  button.textContent = button.dataset.adding || label;

  const added = await addLines(button, lines);

  button.textContent = (added ? button.dataset.added : button.dataset.failed) || label;
  setTimeout(() => {
    button.textContent = label;
    button.disabled = false;
  }, 1800);
}

document.addEventListener('click', (event) => {
  const button = /** @type {HTMLElement} */ (event.target).closest?.('button[data-pg-add]');
  if (!(button instanceof HTMLButtonElement) || !button.dataset.variantId) return;

  // Modal dialogs close first so the cart drawer can take focus when it opens.
  button.closest('dialog')?.close();
  addFromButton(button, [{ id: Number(button.dataset.variantId), quantity: 1 }]);
});

/** Scroll-snap carousel with arrows, dots and optional autoplay. */
class PgCarousel extends Component {
  #abort = new AbortController();
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.track?.addEventListener('scroll', this.#syncDots, { signal, passive: true });
    this.addEventListener('pointerenter', this.#stop, { signal });
    this.addEventListener('pointerleave', this.#start, { signal });
    this.addEventListener('focusin', this.#stop, { signal });
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#stop();
  }

  get track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
  }

  get slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.track?.children ?? []));
  }

  get index() {
    const left = this.track?.scrollLeft ?? 0;
    const slides = this.slides;
    let closest = 0;
    slides.forEach((slide, i) => {
      if (Math.abs(slide.offsetLeft - left) < Math.abs(slides[closest].offsetLeft - left)) closest = i;
    });
    return closest;
  }

  /** @param {number} index */
  goTo(index) {
    const slides = this.slides;
    if (!slides.length) return;
    const target = slides[(index + slides.length) % slides.length];
    this.track?.scrollTo({ left: target.offsetLeft, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  /** Set by the pause button; hover and focus never restart a paused slider. */
  #paused = false;

  /** @param {Event} event */
  #onClick = (event) => {
    const toggle = /** @type {HTMLElement} */ (event.target).closest('[data-toggle-play]');
    if (toggle) {
      this.#paused = !this.#paused;
      toggle.setAttribute('aria-pressed', String(this.#paused));
      if (this.#paused) this.#stop();
      else this.#start();
      return;
    }

    const control = /** @type {HTMLElement} */ (event.target).closest('[data-go]');
    if (!(control instanceof HTMLElement)) return;
    const go = control.dataset.go;
    if (go === 'prev') this.goTo(this.index - 1);
    else if (go === 'next') this.goTo(this.index + 1);
    else this.goTo(Number(go));
  };

  #syncDots = () => {
    const current = this.index;
    this.querySelectorAll('[data-dot]').forEach((dot, i) => {
      dot.setAttribute('aria-current', String(i === current));
    });
    const counter = this.querySelector('[data-counter]');
    if (counter) counter.textContent = String(current + 1);
  };

  #start = () => {
    const delay = Number(this.dataset.autoplay);
    if (!delay || this.#paused || prefersReducedMotion() || this.slides.length < 2) return;
    this.#stop();
    this.#timer = window.setInterval(() => this.goTo(this.index + 1), delay);
  };

  #stop = () => {
    window.clearInterval(this.#timer);
  };
}

/** Moving rail of cards; each card opens a pop-up with its video and Add to Cart. */
class PgRail extends PgCarousel {
  #abortRail = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onOpen, { signal: this.#abortRail.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortRail.abort();
  }

  get dialog() {
    return this.querySelector('dialog');
  }

  /** @param {Event} event */
  #onOpen = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const dialog = this.dialog;
    if (!dialog) return;

    if (target.closest('[data-close]') || target === dialog) {
      dialog.close();
      return;
    }

    const card = target.closest('[data-card]');
    const template = card?.querySelector('template');
    const body = dialog.querySelector('[data-body]');
    if (!template || !body) return;

    body.replaceChildren(template.content.cloneNode(true));
    dialog.showModal();
    dialog.querySelector('video')?.play().catch(() => {});
    dialog.addEventListener('close', () => body.replaceChildren(), { once: true });
  };
}

/** Swaps the model image by skin tone and shade, and keeps Add to Cart on the chosen shade. */
class PgShadeFinder extends Component {
  #abort = new AbortController();
  #tone = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const tone = target.closest('[data-tone]');
    const shade = target.closest('[data-shade]');
    if (tone instanceof HTMLElement) {
      this.#tone = Number(tone.dataset.tone);
      this.#select('[data-tone]', tone);
      this.#render();
    } else if (shade instanceof HTMLElement) {
      this.#select('[data-shade]', shade);
      this.#render();
    }
  };

  /**
   * @param {string} selector
   * @param {HTMLElement} active
   */
  #select(selector, active) {
    this.querySelectorAll(selector).forEach((item) => {
      item.setAttribute('aria-pressed', String(item === active));
    });
  }

  #render() {
    const shade = /** @type {HTMLElement | null} */ (this.querySelector('[data-shade][aria-pressed="true"]'));
    if (!shade) return;

    const image = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-main]'));
    const src = shade.dataset[`image${this.#tone}`];
    if (image && src) {
      image.src = src;
      image.removeAttribute('srcset');
      image.alt = shade.dataset.name || '';
    }

    const name = this.querySelector('[data-name]');
    const price = this.querySelector('[data-price]');
    if (name) name.textContent = shade.dataset.name || '';
    if (price) price.textContent = shade.dataset.price || '';

    const add = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-pg-add]'));
    if (add) {
      add.dataset.variantId = shade.dataset.variantId;
      add.disabled = shade.dataset.available !== 'true';
      add.textContent = add.disabled ? add.dataset.soldOut || '' : add.dataset.label || '';
    }
  }
}

/** Virtual try-on: swaps the model photo by skin tone and the lips layer by shade. */
class PgTryon extends Component {
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const tone = target.closest('[data-tone-id]');
    const shade = target.closest('[data-shade-id]');
    if (tone instanceof HTMLElement) this.dataset.tone = tone.dataset.toneId;
    else if (shade instanceof HTMLElement) this.dataset.shade = shade.dataset.shadeId;
    else return;
    this.#render();
  };

  #render() {
    const { tone, shade, assetPrefix } = this.dataset;
    const toneButton = /** @type {HTMLElement | null} */ (this.querySelector(`[data-tone-id="${tone}"]`));
    const shadeButton = /** @type {HTMLElement | null} */ (this.querySelector(`[data-shade-id="${shade}"]`));
    if (!toneButton || !shadeButton) return;

    this.querySelectorAll('[data-tone-id], [data-shade-id]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button === toneButton || button === shadeButton));
    });

    const base = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-base]'));
    const baseMobile = /** @type {HTMLSourceElement | null} */ (this.querySelector('[data-base-mobile]'));
    if (base && toneButton.dataset.desktop) {
      base.src = toneButton.dataset.desktop;
      base.alt = toneButton.dataset.label || '';
    }
    if (baseMobile && toneButton.dataset.mobile) baseMobile.srcset = toneButton.dataset.mobile;

    const lips = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-lips]'));
    const lipsMobile = /** @type {HTMLSourceElement | null} */ (this.querySelector('[data-lips-mobile]'));
    if (lips) {
      lips.setAttribute('data-loading', '');
      lips.addEventListener('load', () => lips.removeAttribute('data-loading'), { once: true });
      lips.addEventListener('error', () => lips.removeAttribute('data-loading'), { once: true });
      if (lipsMobile) lipsMobile.srcset = `${assetPrefix}pg-tryon-${tone}-${shade}-m.png`;
      lips.src = `${assetPrefix}pg-tryon-${tone}-${shade}.png`;
    }

    const set = (/** @type {string} */ selector, /** @type {string} */ text) => {
      const element = this.querySelector(selector);
      if (element) element.textContent = text;
    };
    set('[data-tone-label]', toneButton.dataset.label || '');
    set('[data-name]', shadeButton.dataset.name || '');
    set('[data-price]', shadeButton.dataset.price || '');

    const knowMore = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-know-more]'));
    if (knowMore && shadeButton.dataset.url) knowMore.href = shadeButton.dataset.url;

    const add = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-pg-add]'));
    if (add) {
      const { variantId, available } = shadeButton.dataset;
      add.dataset.variantId = variantId || '';
      add.disabled = !variantId || available !== 'true';
      add.textContent = !variantId
        ? add.dataset.comingSoon || ''
        : available === 'true'
          ? add.dataset.label || ''
          : add.dataset.soldOut || '';
    }
  }
}

/** Lets the shopper fill a fixed-size box and adds every pick to the cart together. */
class PgBuildBox extends Component {
  #abort = new AbortController();
  /** @type {{ id: number, title: string }[]} */
  #picks = [];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick, { signal: this.#abort.signal });
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
  }

  get size() {
    return Number(this.dataset.size) || 3;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const pick = target.closest('[data-pick]');
    const remove = target.closest('[data-remove]');
    const submit = target.closest('[data-submit]');

    if (pick instanceof HTMLElement && this.#picks.length < this.size) {
      this.#picks.push({ id: Number(pick.dataset.pick), title: pick.dataset.title || '' });
      this.#render();
    } else if (remove instanceof HTMLElement) {
      this.#picks.splice(Number(remove.dataset.remove), 1);
      this.#render();
    } else if (submit instanceof HTMLButtonElement && this.#picks.length === this.size) {
      this.#submit(submit);
    }
  };

  /** @param {HTMLButtonElement} button */
  async #submit(button) {
    /** @type {Map<number, number>} */
    const quantities = new Map();
    for (const { id } of this.#picks) quantities.set(id, (quantities.get(id) ?? 0) + 1);

    const properties = { _box: this.dataset.boxName || 'Build your box' };
    await addFromButton(
      button,
      Array.from(quantities, ([id, quantity]) => ({ id, quantity, properties }))
    );
    this.#picks = [];
    this.#render();
  }

  #render() {
    const slots = this.querySelector('[data-slots]');
    if (slots) {
      slots.replaceChildren(
        ...Array.from({ length: this.size }, (_, i) => {
          const pick = this.#picks[i];
          const slot = document.createElement(pick ? 'button' : 'span');
          slot.className = 'pg-box__slot';
          slot.textContent = pick ? pick.title : String(i + 1);
          if (slot instanceof HTMLButtonElement) {
            slot.type = 'button';
            slot.dataset.remove = String(i);
            slot.setAttribute('aria-label', `${this.dataset.removeLabel}: ${pick?.title}`);
          }
          return slot;
        })
      );
    }

    const full = this.#picks.length >= this.size;
    this.querySelectorAll('[data-pick]').forEach((button) => {
      /** @type {HTMLButtonElement} */ (button).disabled = full;
    });
    const submit = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-submit]'));
    if (submit) submit.disabled = !full;
  }
}

/** Login-gated prize wheel. One spin per customer on this device. */
class PgSpinWheel extends Component {
  #abort = new AbortController();
  /** @type {number | undefined} */
  #openTimer;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick, { signal: this.#abort.signal });

    const saved = this.#saved();
    if (saved !== null) this.#showResult(saved);

    if (new URLSearchParams(location.search).has('spin')) {
      this.open();
    } else if (!document.documentElement.classList.contains('shopify-design-mode') && !this.#seen()) {
      this.#openTimer = window.setTimeout(() => this.open(), Number(this.dataset.delay) * 1000);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    window.clearTimeout(this.#openTimer);
  }

  get dialog() {
    return this.querySelector('dialog');
  }

  get prizes() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-prize]')));
  }

  get storageKey() {
    return `pg-spin-${this.dataset.customer}`;
  }

  open() {
    if (this.dialog?.open) return;
    this.dialog?.showModal();
    try {
      sessionStorage.setItem('pg-spin-seen', '1');
    } catch {}
  }

  #seen() {
    try {
      return sessionStorage.getItem('pg-spin-seen') === '1';
    } catch {
      return false;
    }
  }

  #saved() {
    try {
      const value = localStorage.getItem(this.storageKey);
      return this.dataset.customer && value !== null ? Number(value) : null;
    } catch {
      return null;
    }
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-open]')) this.open();
    else if (target.closest('[data-close]') || target === this.dialog) this.dialog?.close();
    else if (target.closest('[data-spin]')) this.#spin();
    else if (target.closest('[data-copy]')) this.#copy();
  };

  #pick() {
    const prizes = this.prizes;
    const total = prizes.reduce((sum, prize) => sum + Number(prize.dataset.weight), 0);
    let roll = Math.random() * total;
    for (const [i, prize] of prizes.entries()) {
      roll -= Number(prize.dataset.weight);
      if (roll < 0) return i;
    }
    return 0;
  }

  #spin() {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-spin]'));
    const wheel = /** @type {HTMLElement | null} */ (this.querySelector('[data-wheel]'));
    if (!button || !wheel) return;

    button.disabled = true;
    const index = this.#pick();
    const segment = 360 / this.prizes.length;
    wheel.style.transform = `rotate(${360 * 6 - (index + 0.5) * segment}deg)`;

    window.setTimeout(
      () => {
        try {
          localStorage.setItem(this.storageKey, String(index));
        } catch {}
        this.#showResult(index);
      },
      prefersReducedMotion() ? 0 : 5200
    );
  }

  /** @param {number} index */
  #showResult(index) {
    const prize = this.prizes[index];
    const result = this.querySelector('[data-result]');
    if (!prize || !result) return;

    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-spin]'));
    if (button) button.hidden = true;
    result.removeAttribute('hidden');

    const title = result.querySelector('[data-result-title]');
    const note = result.querySelector('[data-result-note]');
    const code = /** @type {HTMLElement | null} */ (result.querySelector('[data-copy]'));
    const apply = /** @type {HTMLAnchorElement | null} */ (result.querySelector('[data-apply]'));

    if (title) title.textContent = prize.dataset.label || '';
    if (note) note.textContent = prize.dataset.note || '';

    const value = prize.dataset.code || '';
    if (code) {
      code.hidden = !value;
      code.textContent = value;
    }
    if (apply) {
      apply.hidden = !value || prize.dataset.later === 'true';
      apply.href = `/discount/${encodeURIComponent(value)}?redirect=${encodeURIComponent(Theme.routes.cart_url)}`;
    }
  }

  async #copy() {
    const code = this.querySelector('[data-copy]');
    try {
      await navigator.clipboard.writeText(code?.textContent ?? '');
      code?.setAttribute('data-copied', 'true');
    } catch {}
  }
}

const elements = {
  'pg-carousel': PgCarousel,
  'pg-rail': PgRail,
  'pg-shade-finder': PgShadeFinder,
  'pg-tryon': PgTryon,
  'pg-build-box': PgBuildBox,
  'pg-spin-wheel': PgSpinWheel,
};

for (const [name, element] of Object.entries(elements)) {
  if (!customElements.get(name)) customElements.define(name, element);
}
